import clsx from 'clsx'
import React, { useState, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { MenuItem } from '@headlessui/react'

import { BespokePanel } from "../../lib/fragments/BespokePanel"
import { generateUniqueId, persistedPageSize, RouteHydration } from "../../utils/Helpers"
import { PEOPLE_PROFILE } from "../../api/Registry"
import HttpServices from "../../services/HttpServices"
import { PhaseManifest } from '../../utils/Interfaces'
import { generateDefaultValue, initializePhasing, resolveManifestLogic } from '../../lib/phases/PhaseHelper'
import { ActionButton } from '../../components/utils/ActionButton'
import { DownloadIcon } from '../../resources/icons/DownloadIcon'
import { UploadIcon } from '../../resources/icons/UploadIcon'
import DropDown from '../../lib/fragments/DropDown'
import ReactTable from '../../lib/fragments/ReactTable'
import { InterstitialZone } from './wizard/InterstitialZone'

export const EditProfileDefinition = ({ show, showOrHide, uuid }) => {
    const [state, setstate] = useState({
        posting: false,
        loading: true,
        httpStatus: 200,
        status: 'pending',
        data: {
            group: null,
            options: null,
            manifest: [],
            links: [],
            meta: {
                to: 0,
                from: 0,
                total: 0,
                lastPage: null,
                currentPage: null,
                lastPageUrl: null,
            }
        },
        input: [],
        faults: [],
    })

    const [displayColumns, setDisplayColumns] = useState([
        { id: '0', key: 'a', label: 'Label', default: true },
        { id: '1', key: 'b', label: 'Hint', default: false },
        { id: '2', key: 'z', label: '-', default: true },

    ])

    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

    const onCloseModal = async () => {
        showOrHide()

        // Reset the state on close
        setTimeout(() => {
            setstate((prev: any) => ({
                ...prev,
                posting: false,
                loading: true,
                httpStatus: 200,
                status: 'pending',
                data: {
                    group: null,
                    options: null,
                    manifest: [],
                    links: [],
                    meta: {
                        to: 0,
                        from: 0,
                        total: 0,
                        lastPage: null,
                        currentPage: null,
                        lastPageUrl: null,
                    }
                },
                input: [],
                faults: [],
            }))
        }, 300)
    }

    useEffect(() => {
        if (show) {
            setVisibleColumns(displayColumns.map((c) => c.key));
            fetchProfileDefinitionDetails()
        }
    }, [show])

    const fetchProfileDefinitionDetails = async (navigateTo?: string | null) => {
        let { status, httpStatus, data } = state

        // Generate initial input & error buckets
        // This creates the { [slug]: {} } structure for every phase
        const initialInput: any = [];
        const initialFaults: any = [];

        try {
            const pageSize = persistedPageSize();

            // Define the Base Routes with standard template replacement
            // No need for .reduce if we are only hydrating one ":definition" tag
            const definitionDetailsRoute = RouteHydration(PEOPLE_PROFILE.DEFINITION_DETAILS, ":definition", uuid);
            const definitionOptionsRoute = `${RouteHydration(PEOPLE_PROFILE.DEFINITION_OPTIONS, ":definition", uuid)}?records=${pageSize}`;

            const [definitionRes, optionsRes, manifestRes]: any[] = await Promise.all([
                HttpServices.httpGet(definitionDetailsRoute),
                HttpServices.httpGet(definitionOptionsRoute),
                HttpServices.httpGet(PEOPLE_PROFILE.DEFINIT_MANIFEST),
            ]);

            const responses = [definitionRes.status, manifestRes.status, optionsRes.status];

            if (responses.every(res => res === 200)) {
                const optionsBlock = optionsRes.data
                data.group = definitionRes.data.group
                data.manifest = manifestRes.data.manifest.flatMap((m: any) => m.definitions);

                data.options = optionsBlock.data
                data.links = optionsBlock.links

                data.meta.to = optionsBlock.to
                data.meta.from = optionsBlock.from
                data.meta.total = optionsBlock.total
                data.meta.lastPage = optionsBlock.last_page
                data.meta.currentPage = optionsBlock.current_page
                data.meta.lastPageUrl = optionsBlock.last_page_url

                const phaseManifest: PhaseManifest[] = manifestRes.data.manifest.map((manifest: any, index: number) => {
                    // Initialize the buckets for each specific slug
                    const phaseInputs = {}
                    const phaseFaults = {}

                    // Loop through the elements (attr.s) to extract input IDs
                    // Assume 'g' is the input object containing the ID
                    manifest.definitions.forEach((definition: any) => {
                        if (!definition.type.includes('sub-header', 'divider')) {
                            // Resolve Default Values
                            const displayType = definition.type;
                            let defaultValue: any = generateDefaultValue(definition)

                            // Set the default value for each input ID
                            phaseInputs[definition.name] = defaultValue;
                            phaseFaults[definition.name] = '';
                        }
                    });

                    // PUSH the keyed object into the state arrays
                    initialInput.push({ [manifest.slug]: phaseInputs });
                    initialFaults.push({ [manifest.slug]: phaseFaults });

                    return ({
                        gid: generateUniqueId(manifest.slug),
                        sequence: index,
                        slug: manifest.slug,
                        title: manifest.title,
                        caption: manifest.caption,
                        module: manifest.strategy,
                    })
                });

                // Initialize the Phase State Engine
                const newPhaseState = initializePhasing(phaseManifest, phaseManifest[0]?.slug);
                console.log('MANIFES', data.manifest);

                httpStatus = 200
                status = 'fulfilled'
            } else {
                httpStatus = 500
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
            console.log(error)
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            loading: false,
            data: {
                ...prevState.data,
            },
        }));
    }

    const onPageSizeChangeHandler = (pageSize: any) => {
        if (state.loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        persistedPageSize(pageSize)
        fetchProfileDefinitionDetails()
    }

    const pageNavigationHandler = (navigateTo: string) => {
        if (state.loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        fetchProfileDefinitionDetails(navigateTo)
    }

    const toggleColumnVisibility = (displayColumn: any) => {
        const key = displayColumn?.key
        if (displayColumn.default) return;

        setVisibleColumns((prev) => {
            let updated: string[];

            if (prev.includes(key)) {
                // remove key
                updated = prev.filter((col) => col !== key);
            } else {
                // add but enforce max 6 (inclusive of z)
                if (prev.length >= 6) {
                    return prev; // no change
                }

                updated = [...prev, key];
            }

            // always put z last if it exists
            if (updated.includes("z")) {
                updated = updated.filter((col) => col !== "z");
                updated.push("z");
            }

            return updated;
        });
    };

    const reorderedDisplayColumns = async (newDisplayList: any[]) => {
        if (state.loading) return

        setDisplayColumns(newDisplayList);

        setVisibleColumns(prevVisible => {
            // Extract keys from the new order
            const orderedKeys = newDisplayList.map(c => c.key);

            // Keep only keys that were already visible
            const filteredAndOrdered = orderedKeys.filter(key => prevVisible.includes(key));

            // Return the reordered, valid list
            return filteredAndOrdered;
        });

        console.log('Reordered →', newDisplayList);
    }



    const columnarLedger = useMemo(
        () => [
            {
                key: 'a',
                Header: 'Label',
                id: generateUniqueId('label'),
                accessor: (data: { label: string, priority: string, enabled: boolean }) => {
                    return (
                        <div className={clsx(
                            !data.enabled && "opacity-60 grayscale-[0.5]",
                            "text-slate-700 dark:text-slate-200 md:block truncate text-ellipsis overflow-hidden whitespace-nowrap"
                        )}>
                            {data.priority === 'pinned' ? '📌' : ''} {data.label}
                        </div>
                    )
                }
            },
            {
                key: 'b',
                Header: 'Hint',
                id: generateUniqueId('hint'),
                accessor: (data: { hint: string, enabled: boolean }) => {
                    return (
                        <div className={clsx(
                            !data.enabled && "opacity-60 grayscale-[0.5]",
                            "text-slate-700 dark:text-slate-200 md:block truncate text-ellipsis overflow-hidden whitespace-nowrap"
                        )}>
                            <span className="block text-slate-500 dark:text-slate-400">
                                {data.hint}
                            </span>
                        </div>
                    )
                }
            },
            {
                key: 'z',
                Header: '-',
                id: generateUniqueId('-'),
                accessor: (data: { uuid: any, enabled: boolean }) => (
                    <div className="lg:flex flex flex-row-reverse align-middle items-center gap-x-2 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0 md:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="flex-none">
                            <DropDown
                                type={'borderless'}
                                items={
                                    <div className="w-40">
                                        <MenuItem>
                                            <button type="button" /* onClick={() => navigateToEditUserAttribute(attr)} */ className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                Edit Details
                                            </button>
                                        </MenuItem>

                                        <MenuItem>
                                            <button type="button" /* onClick={() => navigateToEditUserAttribute(attr)} */ className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                Pin to Top
                                            </button>
                                        </MenuItem>

                                        <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                        {
                                            data.enabled ? (
                                                <MenuItem>
                                                    <button type="button" /* onClick={() => toggleModal('disableProfileGroup', true, state.activeProfileGroup.g)} */
                                                        className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                        Disable
                                                    </button>
                                                </MenuItem>
                                            ) : (
                                                <MenuItem>
                                                    <button type="button" /* onClick={() => attributeGroupStatusHandler(state.activeProfileGroup.g, 'enable')} */ className="w-full text-emerald-600 rounded-md px-3 py-2 text-sm data-[focus]:bg-emerald-200 dark:data-[focus]:bg-emerald-800/40 dark:data-[focus]:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800/40 text-start">
                                                        Enable
                                                    </button>
                                                </MenuItem>
                                            )
                                        }
                                    </div>
                                }
                            />
                        </div>
                    </div>
                ),
            },
        ], []
    )

    console.log('columnarLedger', columnarLedger);

    return (
        <React.Fragment>
            <BespokePanel
                show={show}
                title={
                    state.status === 'fulfilled' && (
                        <ul className="w-full flex flex-wrap items-center text-xs text-stone-500 font-normal dark:text-gray-400 tracking-wide">
                            <li>
                                <span className="h-5 flex gap-x-3 items-center">
                                    People Profile Group
                                </span>
                            </li>
                            <li className="ml-2 breadcrumb-item">
                                <span className="fa-light fa-chevron-right text-muted"></span>
                            </li>

                            <li className="ml-2 breadcrumb-item">
                                <span className="lttr-spc">{state.data.group.name}</span>
                            </li>
                        </ul>
                    )
                }
                status={state.status}
                showOrHide={onCloseModal}
                httpStatus={state.httpStatus}
                size={
                    state.data.group?.definition?.config?.type === 'predefined'
                        ? '6xl'
                        : 'lg'
                }
                components={
                    state.status === 'fulfilled' && (
                        <div className="flex md:flex-row flex-col align-middle h-full md:overflow-hidden">
                            <aside className={clsx(
                                state.data.group.definition.config?.type === 'predefined' ? "md:w-2/5" : "md:w-full",
                                "px-4 w-full bg-gray-50 border-r border-gray-200 px- md:h-full py-2"
                            )}>
                                <div className="md:sticky md:top-0">
                                    {
                                        state.data.manifest.map((definition: any, index: number) => {
                                            const config = state.data.group.definition?.config

                                            const showDefinition = definition?.visibility
                                                ? resolveManifestLogic(definition?.visibility, config)
                                                : true;

                                            if (definition?.title === 'Identity' || !showDefinition) return

                                            switch (definition.name) {
                                                case 'name':
                                                    return (
                                                        <h2 className="text-stone-900 dark:text-white text-lg md:text-lg font-medium flex align-middle items-center gap-x-2">
                                                            {state.data.group.definition.name}
                                                        </h2>
                                                    )

                                                case 'hint':
                                                    return (
                                                        <p className="text-stone-600 dark:text-stone-300 py-2 md:pt-4 lg:pt-2 text-sm" dangerouslySetInnerHTML={{ __html: state.data.group.definition.hint ?? '-' }} />
                                                    )
                                            }
                                        })
                                    }
                                </div>
                            </aside>

                            {
                                state.data.group.definition.config?.type === 'predefined' && (
                                    <main className="h-full px-5 w-full md:w-3/5 md:overflow-y-auto">
                                        <div className="w-full pb-3 mb-2">
                                            <h2 className="text-slate-500 dark:text-white text-base md:text-base font-medium flex align-middle items-center gap-x-2 mb-3">
                                                Options Management
                                            </h2>

                                            <div className="w-full pb-1 mb-2">
                                                <h4 className="text-sm font-bold uppercase tracking-widest text-orange-400 dark:text-orange-500">
                                                    Batch Operations
                                                </h4>

                                                <p className="text-stone-600 dark:text-stone-300 md:pt-1 lg:pt-1 text-sm">
                                                    Synchronize your datasets via a CSV template.
                                                </p>
                                            </div>

                                            <div className="w-full flex align-middle items-center gap-3">
                                                <ActionButton
                                                    posting={false}
                                                    variant={"outline"}
                                                    actionName={
                                                        <div className="flex align-middle items-center gap-2">
                                                            <DownloadIcon />

                                                            <span className="md:block hidden">Template</span>
                                                            <span className="md:hidden inline-block">Download</span>
                                                        </div>
                                                    }
                                                />

                                                <ActionButton
                                                    posting={false}
                                                    actionName={
                                                        <div className="flex align-middle items-center gap-2">
                                                            <UploadIcon />

                                                            <span className="md:block hidden">Upload</span>
                                                            <span className="md:hidden inline-block">Upload</span>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="w-full">
                                            <div className="w-full pb-1 mb-2">
                                                <h4 className="text-sm font-bold uppercase tracking-widest text-orange-400 dark:text-orange-500">
                                                    List Manager
                                                </h4>
                                            </div>

                                            <div className="w-full pb-7">
                                                <ReactTable
                                                    columns={columnarLedger}
                                                    manifest={state.data.options}
                                                    loading={state.loading}
                                                    pageHandler={onPageSizeChangeHandler}
                                                    meta={state.data.meta}
                                                    links={state.data.links}
                                                    navigation={pageNavigationHandler}
                                                    input={state.input}
                                                    showPageSize={true}
                                                    visibleColumns={visibleColumns}
                                                    toggleColumn={toggleColumnVisibility}
                                                    display={displayColumns}
                                                    reorderfunc={reorderedDisplayColumns}
                                                    interstitial_zone={
                                                        <InterstitialZone />
                                                    }
                                                />
                                            </div>
                                        </div>


                                        <div className="w-full relative border-2 border-slate-300 border-dashed rounded-md p-3" id="dropzone">
                                            <input type="file" className="absolute inset-0 w-full h-full opacity-0 z-50" />
                                            <div className="text-center">
                                                <img className="mx-auto h-10 w-10" src="https://www.svgrepo.com/show/357902/image-upload.svg" alt="" />

                                                <h3 className="mt-2 text-sm font-medium text-slate-600">
                                                    <label htmlFor="file-upload" className="relative cursor-pointer">
                                                        <span>Drag and drop</span>
                                                        <span className="text-orange-600"> or browse </span>
                                                        <span>to upload</span>
                                                        <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                                                    </label>
                                                </h3>
                                                <p className="mt-1 text-xs text-gray-500">
                                                    PNG, JPG, GIF up to 10MB
                                                </p>
                                            </div>

                                            <img src="" className="mt-4 mx-auto max-h-40 hidden" id="preview" />
                                        </div>










                                    </main>
                                )
                            }
                        </div>
                    )}
            />
        </React.Fragment>
    )
}